import { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { Field } from "react-final-form";
import PropTypes from "prop-types";

import PhoneInput from "@components/Form/PhoneInput";
import { ROMANIA_COUNTRY_CODE } from "@lib/phoneNumber";
import { getPhoneCountries } from "@store/user/actions";
import {
  isMobileNumberForCountry,
  isPhoneNumberForCountry,
  isRequired,
} from "@lib/validations";

// No default, on purpose: a boolean would be silently wrong on half the surfaces
// whichever way it defaulted.
export const PHONE_ACCEPTS = {
  // Registration, the 426 gate, account addresses.
  FIXED_OR_MOBILE: "fixed-or-mobile",
  // The SMS modal only: it sends a code, so a landline cannot complete the flow.
  MOBILE_ONLY: "mobile-only",
};

const VALIDATOR_BY_ACCEPTS = {
  [PHONE_ACCEPTS.FIXED_OR_MOBILE]: isPhoneNumberForCountry,
  [PHONE_ACCEPTS.MOBILE_ONLY]: isMobileNumberForCountry,
};

// Derived from `accepts` like the validator, so the copy cannot drift from the
// rule it describes. A caller can still override with its own `tooltip`.
const TOOLTIP_BY_ACCEPTS = {
  [PHONE_ACCEPTS.FIXED_OR_MOBILE]: (
    <>
      Sunt acceptate numere de telefon fix sau mobil. Alege prefixul tarii din
      lista, apoi introdu numarul fara prefixul international.
      <br />
      Ex. pentru Romania: 07XXXXXXXX; 02XXXXXXXX.
    </>
  ),
  [PHONE_ACCEPTS.MOBILE_ONLY]: (
    <>
      Sunt acceptate doar numere de telefon mobil, pentru ca pe ele trimitem
      codul prin SMS. Alege prefixul tarii din lista, apoi introdu numarul fara
      prefixul international.
      <br />
      Ex. pentru Romania: 07XXXXXXXX.
    </>
  ),
};

// Drop-in replacement for a <Field name="telephone" component={InputText} />.
//
// react-final-form 4.1.0 has no field hooks, so the country comes from an outer
// <Field> render-prop rather than form.change(). That is what lets this work in
// class components too.
const PhoneField = ({
  name = "telephone",
  countryFieldName = "telephone_country_code",
  accepts,
  required = false,
  tooltip,
  // Without this, the account phone list. Pass the DHL delivery list when the
  // country being asked about is the address's.
  countryOptions,
  // false renders the prefix as a read-only indicator: on a DHL address the
  // country is the address's, not the customer's to choose here.
  countryEditable = true,
  phoneCountries,
  getPhoneCountries,
  // Swallowed, never forwarded: a stray validate= would silently replace the
  // country-aware rule.
  validate: callerValidate,
  ...rest
}) => {
  const options = countryOptions ?? phoneCountries;

  // Keyed on the resolved options, so a caller passing its own list never fetches
  // one it will not use.
  useEffect(() => {
    if (!options) {
      getPhoneCountries();
    }
  }, [options, getPhoneCountries]);

  const resolvedValidator = VALIDATOR_BY_ACCEPTS[accepts];

  // Throw in dev rather than warn: a misspelled `accepts` would otherwise pick
  // the permissive fallback below unnoticed.
  if (process.env.NODE_ENV !== "production") {
    if (!resolvedValidator) {
      throw new Error(
        `PhoneField: \`accepts\` must be one of ${Object.values(
          PHONE_ACCEPTS
        ).join(", ")} - got ${JSON.stringify(accepts)}.`
      );
    }

    if (callerValidate) {
      throw new Error(
        "PhoneField: `validate` is owned by PhoneField. Use `accepts` to pick the rule."
      );
    }
  }

  // In production prefer the permissive rule over crashing the page.
  const validator = resolvedValidator ?? isPhoneNumberForCountry;

  // Country read from allValues, NOT closed over: pasting a foreign number changes
  // the country and the number in one handler, before React re-renders, so a
  // captured country would still be the previous one. composeFormValidators is
  // avoided because it drops allValues.
  const validate = useCallback(
    (value, allValues) => {
      if (required) {
        const requiredError = isRequired()(value);

        if (requiredError) {
          return requiredError;
        }
      }

      const country =
        (allValues && allValues[countryFieldName]) || ROMANIA_COUNTRY_CODE;

      return validator(country)(value);
    },
    [countryFieldName, required, validator]
  );

  return (
    <Field name={countryFieldName} subscription={{ value: true }}>
      {({ input: countryInput }) => (
        <Field
          {...rest}
          name={name}
          component={PhoneInput}
          required={required}
          tooltip={tooltip ?? TOOLTIP_BY_ACCEPTS[accepts]}
          countryCode={countryInput.value || ROMANIA_COUNTRY_CODE}
          countryOptions={options}
          countryEditable={countryEditable}
          onCountryChange={countryInput.onChange}
          validate={validate}
        />
      )}
    </Field>
  );
};

PhoneField.propTypes = {
  accepts: PropTypes.oneOf(Object.values(PHONE_ACCEPTS)).isRequired,
  name: PropTypes.string,
  countryFieldName: PropTypes.string,
  countryOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  countryEditable: PropTypes.bool,
  required: PropTypes.bool,
};

export default connect(
  (state) => ({ phoneCountries: state.user.phoneCountries }),
  { getPhoneCountries }
)(PhoneField);
